import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Spinner } from "../../../atoms/Spinner";
import { ModalShell } from "../../../atoms/ModalShell";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  HiOutlineDocumentText,
  HiOutlineBan,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineExclamationCircle,
  HiOutlineXCircle,
} from "react-icons/hi";
import { IconType } from "react-icons";
import { CustomButton } from "../../../atoms/CustomButton";
import { EmptyState } from "../../../atoms/EmptyState";
import { StatusBadge, BadgeTone } from "../../../atoms/StatusBadge";
import ActionButton from "../../../atoms/ActionButton";
import CopyToClipboardButton from "../../../atoms/CopyToClipboardButton";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import UpgradeModal from "../../../modals/UpgradeModal";
import { PremiumBenefit } from "../../../atoms/UpgradePromptContent";
import { PlanTier } from "../../../../utils/permissions";
import {
  useListSigningRequests,
  useCancelSigningRequest,
  useDeleteSigningRequest,
  useGetSigningLink,
  useGetSignedDocumentUrl,
} from "../../../../hooks/esignature";
import moment from "moment";
import "moment/locale/fr";
import { momentLocaleOf } from "../../../../utils/momentLocale";

interface ESignatureSectionProps {
  customerIdentification: string;
  entityIdentification: string;
  userIdentification: string;
  hasESignatureAccess: boolean;
}

type SigningRequestStatus =
  | "pending"
  | "viewed"
  | "review"
  | "signed"
  | "declined"
  | "expired"
  | "cancelled";

interface SigningRequest {
  request_id: string;
  template_id: string;
  template_name?: string | null;
  deletable?: boolean;
  status: SigningRequestStatus;
  expires_at: string;
  audit_trail?: {
    created_at?: string;
    sent_at?: string;
    viewed_at?: string | null;
    signed_at?: string | null;
    declined_at?: string | null;
    cancelled_at?: string | null;
  };
}

const STATUS_CONFIG: Record<
  SigningRequestStatus,
  {
    icon: IconType;
    colorClass: string;
    badgeColor: BadgeTone;
    badgeEmphasis?: "solid";
  }
> = {
  pending: {
    icon: HiOutlineClock,
    colorClass: "text-amber-600",
    badgeColor: "warning",
  },
  viewed: {
    icon: HiOutlineClock,
    colorClass: "text-blue-400",
    badgeColor: "info",
  },
  review: {
    icon: HiOutlineClock,
    colorClass: "text-blue-600",
    badgeColor: "info",
    badgeEmphasis: "solid",
  },
  signed: {
    icon: HiOutlineCheckCircle,
    colorClass: "text-green-600",
    badgeColor: "success",
  },
  declined: {
    icon: HiOutlineXCircle,
    colorClass: "text-red-500",
    badgeColor: "failure",
  },
  expired: {
    icon: HiOutlineClock,
    colorClass: "text-gray-400",
    badgeColor: "gray",
  },
  cancelled: {
    icon: HiOutlineBan,
    colorClass: "text-gray-400",
    badgeColor: "gray",
  },
};

const isCancellable = (req: SigningRequest): boolean =>
  (req.status === "pending" || req.status === "viewed") &&
  new Date(req.expires_at) > new Date();

const EXPIRY_WARNING_DAYS = 3;

const daysBeforeExpiry = (req: SigningRequest): number | null => {
  if (!req.expires_at) return null;
  if (
    req.status !== "pending" &&
    req.status !== "viewed" &&
    req.status !== "review"
  ) {
    return null;
  }
  const days = moment(req.expires_at)
    .startOf("day")
    .diff(moment().startOf("day"), "days");
  return days >= 0 && days <= EXPIRY_WARNING_DAYS ? days : null;
};

const ESignatureSection: React.FC<ESignatureSectionProps> = ({
  customerIdentification,
  entityIdentification,
  userIdentification,
  hasESignatureAccess,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    moment.locale(momentLocaleOf(i18n.language));
  }, [i18n.language]);

  const [showAll, setShowAll] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState<SigningRequest | null>(
    null
  );
  const [cancellingRequest, setCancellingRequest] =
    useState<SigningRequest | null>(null);
  const [signingLinks, setSigningLinks] = useState<Record<string, string>>({});
  const [failedLinks, setFailedLinks] = useState<Set<string>>(new Set());
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const eSignatureBenefits: PremiumBenefit[] = useMemo(
    () => [
      {
        id: "esig-send",
        text: t("dashboard.upgrade.esignature.benefit1") as string,
      },
      {
        id: "esig-track",
        text: t("dashboard.upgrade.esignature.benefit2") as string,
      },
      {
        id: "esig-expiry",
        text: t("dashboard.upgrade.esignature.benefit3") as string,
      },
      {
        id: "esig-collect",
        text: t("dashboard.upgrade.esignature.benefit4") as string,
      },
    ],
    [t]
  );

  const {
    data: requests,
    isLoading,
    isError,
    refetch,
  } = useListSigningRequests(
    customerIdentification,
    entityIdentification,
    userIdentification,
    undefined,
    !hasESignatureAccess
  );

  const cancelMutation = useCancelSigningRequest();
  const deleteMutation = useDeleteSigningRequest();
  const signingLinkMutation = useGetSigningLink();
  const signedDocumentUrlMutation = useGetSignedDocumentUrl();

  const fetchedRef = useRef<Set<string>>(new Set());

  const prefetchSigningLinks = useCallback(async () => {
    if (!requests) return;

    const cancellableRequests = requests.filter(
      (req: SigningRequest) =>
        isCancellable(req) && !fetchedRef.current.has(req.request_id)
    );

    for (const req of cancellableRequests) {
      fetchedRef.current.add(req.request_id);

      try {
        const res = await signingLinkMutation.mutateAsync({
          customer_identification: customerIdentification,
          entityIdentification,
          request_id: req.request_id,
          user_identification: userIdentification,
        });

        const signingUrl = res?.signing_url;
        if (signingUrl) {
          setSigningLinks((prev) => ({
            ...prev,
            [req.request_id]: signingUrl,
          }));
        }
      } catch {
        setFailedLinks((prev) => new Set(prev).add(req.request_id));
      }
    }
  }, [
    requests,
    customerIdentification,
    entityIdentification,
    userIdentification,
  ]);

  useEffect(() => {
    prefetchSigningLinks();
  }, [prefetchSigningLinks]);

  const handleCancel = () => {
    if (!cancellingRequest) return;
    cancelMutation.mutate(
      {
        customer_identification: customerIdentification,
        entityIdentification,
        request_id: cancellingRequest.request_id,
        user_identification: userIdentification,
      },
      {
        onSuccess: () => {
          const id = cancellingRequest.request_id;
          setSigningLinks((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          fetchedRef.current.delete(id);
          setFailedLinks((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setCancellingRequest(null);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!deletingRequest) return;
    deleteMutation.mutate(
      {
        customer_identification: customerIdentification,
        entityIdentification,
        request_id: deletingRequest.request_id,
        user_identification: userIdentification,
      },
      {
        onSuccess: () => {
          const id = deletingRequest.request_id;
          setSigningLinks((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          fetchedRef.current.delete(id);
          setFailedLinks((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setDeletingRequest(null);
        },
      }
    );
  };

  const getCreatedDate = (req: SigningRequest) => {
    const date = req.audit_trail?.created_at || req.audit_trail?.sent_at;
    return date ? moment(date).format("ll") : "—";
  };

  const sortedRequests = React.useMemo(() => {
    if (!requests) return [];
    return [...requests].sort((a: SigningRequest, b: SigningRequest) => {
      const dateA = a.audit_trail?.created_at || a.audit_trail?.sent_at || "";
      const dateB = b.audit_trail?.created_at || b.audit_trail?.sent_at || "";
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [requests]);

  const displayedRequests = showAll
    ? sortedRequests
    : sortedRequests.slice(0, 5);

  const handleRetryFetch = async (requestId: string) => {
    setFailedLinks((prev) => {
      const next = new Set(prev);
      next.delete(requestId);
      return next;
    });
    fetchedRef.current.delete(requestId);

    try {
      const res = await signingLinkMutation.mutateAsync({
        customer_identification: customerIdentification,
        entityIdentification,
        request_id: requestId,
        user_identification: userIdentification,
      });
      const signingUrl = res?.signing_url;
      if (signingUrl) {
        fetchedRef.current.add(requestId);
        setSigningLinks((prev) => ({ ...prev, [requestId]: signingUrl }));
      }
    } catch (err: any) {
      setFailedLinks((prev) => new Set(prev).add(requestId));
      if (!err?.response?.data?.message) {
        toast.error(
          t("dashboard.entities.viewEntityUser.esignature.loadError")
        );
      }
    }
  };

  const handleOpenSignedDocument = (requestId: string) => {
    const tab = window.open("", "_blank");
    signedDocumentUrlMutation.mutate(
      {
        customer_identification: customerIdentification,
        entityIdentification,
        request_id: requestId,
        user_identification: userIdentification,
      },
      {
        onSuccess: (data) => {
          if (tab && data?.signed_url) {
            tab.location.href = data.signed_url;
          } else {
            tab?.close();
            toast.error(
              t("dashboard.entities.viewEntityUser.esignature.viewSignedError")
            );
          }
        },
        onError: () => {
          tab?.close();
        },
      }
    );
  };

  const showNoAccessEmptyState =
    !hasESignatureAccess && (!requests || requests.length === 0);

  return (
    <>
      {showNoAccessEmptyState ? (
        <div className="flex flex-col gap-3 w-full">
          <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
            <h2 className="text-sm font-semibold text-gray-900">
              {t("dashboard.entities.viewEntityUser.esignature.title")}
            </h2>
            <UnlockBadge onClick={() => setShowUpgradeModal(true)} />
          </div>
          <p className="text-sm text-gray-500">
            {t("dashboard.entities.viewEntityUser.esignature.upgradeHint")}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-col gap-3 border-b border-gray-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">
                {t("dashboard.entities.viewEntityUser.esignature.title")}
              </h2>
              {!hasESignatureAccess && (
                <UnlockBadge onClick={() => setShowUpgradeModal(true)} />
              )}
            </div>
            {hasESignatureAccess && (
              <CustomButton
                title={t(
                  "dashboard.entities.viewEntityUser.esignature.requestSignature"
                )}
                onClick={() => navigate("/control-panel/e-signature")}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              />
            )}
          </div>

          {isLoading && (
            <div className="flex justify-center py-6">
              <Spinner size="md" />
            </div>
          )}

          {isError && (
            <div className="text-center py-6">
              <p className="text-sm text-gray-500 mb-3">
                {t("dashboard.entities.viewEntityUser.esignature.loadError")}
              </p>
              <CustomButton
                variant="secondary"
                className="mx-auto"
                title={t("common.retry")}
                onClick={() => refetch()}
              />
            </div>
          )}

          {!isLoading && !isError && sortedRequests.length === 0 && (
            <EmptyState
              icon={<HiOutlineDocumentText size={24} />}
              title={t(
                "dashboard.entities.viewEntityUser.esignature.noRequests"
              )}
            />
          )}

          {!isLoading && !isError && sortedRequests.length > 0 && (
            <>
              <div className="space-y-3">
                {displayedRequests.map((req: SigningRequest) => {
                  const config =
                    STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                  const expiringInDays = daysBeforeExpiry(req);

                  return (
                    <div
                      key={req.request_id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-100 w-full"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200">
                            <HiOutlineDocumentText
                              size={20}
                              className={config.colorClass}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="truncate text-sm font-semibold text-gray-900">
                                {req.template_name ||
                                  t(
                                    "dashboard.entities.viewEntityUser.esignature.requestLabel"
                                  )}{" "}
                                <span className="font-mono text-xs font-normal text-gray-500">
                                  {req.request_id}
                                </span>
                              </span>
                              <StatusBadge
                                color={config.badgeColor}
                                emphasis={config.badgeEmphasis}
                                icon={config.icon}
                                shape="full"
                              >
                                {t(
                                  `dashboard.entities.viewEntityUser.esignature.status.${req.status}`
                                )}
                              </StatusBadge>
                            </div>
                            <div className="flex flex-col gap-0.5 mt-1">
                              <p className="text-xs text-gray-400">
                                {t(
                                  "dashboard.entities.viewEntityUser.esignature.templateLabel"
                                )}{" "}
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/control-panel/e-signature?template=${encodeURIComponent(
                                        req.template_id
                                      )}`
                                    )
                                  }
                                  className="font-mono text-blue-600 underline underline-offset-2 hover:text-blue-700"
                                >
                                  {req.template_id}
                                </button>
                              </p>
                              <p className="text-xs text-gray-400">
                                {t(
                                  "dashboard.entities.viewEntityUser.esignature.created"
                                )}{" "}
                                {getCreatedDate(req)}
                              </p>
                              {req.status === "signed" &&
                              req.audit_trail?.signed_at ? (
                                <p className="text-xs text-gray-400">
                                  {t(
                                    "dashboard.entities.viewEntityUser.esignature.signedAt"
                                  )}{" "}
                                  {moment(req.audit_trail.signed_at).format(
                                    "lll"
                                  )}
                                </p>
                              ) : req.status === "declined" &&
                                req.audit_trail?.declined_at ? (
                                <p className="text-xs text-gray-400">
                                  {t(
                                    "dashboard.entities.viewEntityUser.esignature.declinedAt"
                                  )}{" "}
                                  {moment(req.audit_trail.declined_at).format(
                                    "lll"
                                  )}
                                </p>
                              ) : req.status === "cancelled" &&
                                req.audit_trail?.cancelled_at ? (
                                <p className="text-xs text-gray-400">
                                  {t(
                                    "dashboard.entities.viewEntityUser.esignature.cancelledAt"
                                  )}{" "}
                                  {moment(req.audit_trail.cancelled_at).format(
                                    "lll"
                                  )}
                                </p>
                              ) : (
                                <>
                                  {req.audit_trail?.viewed_at && (
                                    <p className="text-xs text-gray-400">
                                      {t(
                                        "dashboard.entities.viewEntityUser.esignature.viewedAt"
                                      )}{" "}
                                      {moment(req.audit_trail.viewed_at).format(
                                        "lll"
                                      )}
                                    </p>
                                  )}
                                  {req.expires_at &&
                                    req.status !== "declined" &&
                                    (expiringInDays !== null ? (
                                      <span className="inline-flex w-fit items-center gap-1.5 rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                                        <HiOutlineExclamationCircle
                                          className="h-3 w-3 shrink-0"
                                          aria-hidden="true"
                                        />
                                        {expiringInDays === 0
                                          ? t(
                                              "dashboard.entities.viewEntityUser.esignature.expiresToday"
                                            )
                                          : expiringInDays === 1
                                          ? t(
                                              "dashboard.entities.viewEntityUser.esignature.expiresTomorrow"
                                            )
                                          : t(
                                              "dashboard.entities.viewEntityUser.esignature.expiresInDays",
                                              { days: expiringInDays }
                                            )}
                                      </span>
                                    ) : (
                                      <p className="text-xs text-gray-400">
                                        {t(
                                          "dashboard.entities.viewEntityUser.esignature.expires"
                                        )}{" "}
                                        {moment(req.expires_at).format("ll")}
                                      </p>
                                    ))}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {(isCancellable(req) || req.deletable === true) && (
                          <div className="flex flex-col gap-2 flex-shrink-0 ml-14 sm:ml-0">
                            {isCancellable(req) &&
                              (failedLinks.has(req.request_id) ? (
                                <ActionButton
                                  onClick={() =>
                                    handleRetryFetch(req.request_id)
                                  }
                                  colorScheme="red"
                                  title={t("common.retry")}
                                />
                              ) : (
                                <CopyToClipboardButton
                                  textToCopy={signingLinks[req.request_id]}
                                  isLoading={!signingLinks[req.request_id]}
                                  variant="none"
                                  size="sm"
                                  fullWidth={false}
                                  className="text-sm font-medium text-blue-600 bg-gray-100 hover:bg-blue-100 rounded-lg"
                                />
                              ))}
                            {isCancellable(req) && (
                              <ActionButton
                                onClick={() => setCancellingRequest(req)}
                                colorScheme="red"
                                title={t(
                                  "dashboard.entities.viewEntityUser.esignature.cancelBtn"
                                )}
                              />
                            )}
                            {req.deletable === true && (
                              <ActionButton
                                onClick={() => setDeletingRequest(req)}
                                colorScheme="red"
                                title={t(
                                  "dashboard.entities.viewEntityUser.esignature.deleteBtn"
                                )}
                              />
                            )}
                          </div>
                        )}
                      </div>

                      {req.status === "signed" && (
                        <div className="mt-2 ml-14">
                          <CustomButton
                            variant="outline"
                            size="sm"
                            icon="eye"
                            title={t(
                              "dashboard.entities.viewEntityUser.esignature.viewSigned"
                            )}
                            onClick={() =>
                              handleOpenSignedDocument(req.request_id)
                            }
                            isLoading={
                              signedDocumentUrlMutation.isLoading &&
                              signedDocumentUrlMutation.variables
                                ?.request_id === req.request_id
                            }
                            disabled={
                              signedDocumentUrlMutation.isLoading &&
                              signedDocumentUrlMutation.variables
                                ?.request_id === req.request_id
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {sortedRequests.length > 5 && (
                <div className="flex justify-center mt-4">
                  <CustomButton
                    variant="secondary"
                    onClick={() => setShowAll(!showAll)}
                    title={
                      showAll
                        ? t("dashboard.entities.viewEntityUser.viewLess")
                        : t("dashboard.entities.viewEntityUser.viewMore")
                    }
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!showNoAccessEmptyState && (
        <ModalShell
          show={!!deletingRequest}
          onClose={() => setDeletingRequest(null)}
          size="md"
          title={t(
            "dashboard.entities.viewEntityUser.esignature.deleteModal.title"
          )}
          footer={
            <>
              <CustomButton
                title={t("common.cancel")}
                onClick={() => setDeletingRequest(null)}
                variant="secondary"
              />
              <CustomButton
                title={t(
                  "dashboard.entities.viewEntityUser.esignature.deleteModal.confirm"
                )}
                onClick={handleDelete}
                variant="danger"
                isLoading={deleteMutation.isLoading}
                disabled={deleteMutation.isLoading}
              />
            </>
          }
        >
          <p className="text-sm text-gray-600">
            {t(
              "dashboard.entities.viewEntityUser.esignature.deleteModal.message"
            )}
          </p>
        </ModalShell>
      )}

      {!showNoAccessEmptyState && (
        <ModalShell
          show={!!cancellingRequest}
          onClose={() => setCancellingRequest(null)}
          size="md"
          title={t(
            "dashboard.entities.viewEntityUser.esignature.cancelModal.title"
          )}
          footer={
            <>
              <CustomButton
                title={t("common.cancel")}
                onClick={() => setCancellingRequest(null)}
                variant="secondary"
              />
              <CustomButton
                title={t(
                  "dashboard.entities.viewEntityUser.esignature.cancelModal.confirm"
                )}
                onClick={handleCancel}
                variant="danger"
                isLoading={cancelMutation.isLoading}
                disabled={cancelMutation.isLoading}
              />
            </>
          }
        >
          <p className="text-sm text-gray-600">
            {t(
              "dashboard.entities.viewEntityUser.esignature.cancelModal.message"
            )}
          </p>
        </ModalShell>
      )}

      <UpgradeModal
        showModal={showUpgradeModal}
        setShowModal={setShowUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.esignature.title") as string}
        description={t("dashboard.upgrade.esignature.description") as string}
        benefits={eSignatureBenefits}
        icon={HiOutlineDocumentText}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
    </>
  );
};

export default ESignatureSection;
